// ============================================================================
// avecFalhas: estado de cada relatório na coleta ao vivo do Avec e proteção do
// que falhou (A1 do "Dashboard sem zero silencioso", 23/10/2026). Puro: sem
// rede, sem React, sem import.meta.env.
// ============================================================================
// Contrato novo da unidade (só com AVEC_COLETA_ESTADO; desligado nada aparece):
//   _coleta: { v: 1, pedidos: [códigos], falhou: { código: { motivo, tentativas, ids? } },
//              vazio: [códigos com 200 válido sem nenhum total nem linha],
//              herdado: { código: ISO do _fetchedAt da coleta que trouxe o valor },
//              duracaoMs }
//   _falhas: Object.keys(_coleta.falhou).sort()  (atalho para quem lê)
//   _visitasFonte: '0089' | '0002' | null; com ATENDIDOS_SO_COMANDA_SERVICO (23/10/2026):
//     '0089' | '0031p' (comandas únicas do 0031 paginado) | null, e '0002' só como
//     reserva enquanto DASH_SEM_DADO estiver desligada
// Campo cujo relatório falhou vira null (nunca 0). Listas e mapas continuam
// [] / {} (contrato de folha, duelos, merger e servidor) e a falha deles é lida
// em _falhas. Exceção de neutralidade (P2): com o 0089 falhando e o 0002
// respondendo, `visitas` continua com o 0002 como hoje; quem decide "Sem dado"
// é a tela nova (lê '0089' em _falhas). Com ATENDIDOS_SO_COMANDA_SERVICO e a tela
// nova (DASH_SEM_DADO) o 0002 não entra mais (src/utils/atendidosRegua.js) e a
// herança não o reaproveita; sem a tela nova ele segue de reserva, como hoje.
// Códigos: '0031p' = 0031 paginado (≠ 0031 de página única).
// ============================================================================

import { ATENDIDOS_SO_COMANDA_SERVICO, DASH_SEM_DADO } from '../config/featureFlags.js';

// Com DASH_SEM_DADO desligada a coleta ainda usa o 0002 quando a régua nova não
// tem número (src/services/avecSync.js), então a herança dele continua como hoje.
const ATENDIDOS_SEM_0002 = ATENDIDOS_SO_COMANDA_SERVICO && DASH_SEM_DADO;

export const VERSAO_COLETA = 1;

// Números da unidade que viram null quando o relatório falha.
export const CAMPOS_NULAVEIS = {
  '0281': ['faturamento'],
  '0031': ['servicosRealizados'],
  '0002': ['clientesUnicos'],
  '0089': ['comandasServico', 'comandasProdutoServico', 'comandasProduto'],
  '0012': ['clientesNovos'],
  '0041': ['produtosVendidos', 'faturamentoProdutos'],
  '0326': ['agendamentosPreferenciais', 'semPreferencia'],
  '0062': ['pacotesVendidosTotal', 'valorPacotes'],
  '0064': ['_comandasPacoteTotal'],
  '0007': ['taxaRetorno'],
};

// Tudo o que vem de cada relatório (números, listas e mapas): o que é copiado
// quando o valor é herdado de uma coleta anterior do MESMO período/mês.
export const CAMPOS_DO_RELATORIO = {
  '0281': ['faturamento', 'formasPagamento'],
  '0031': ['servicosRealizados', 'porServico'],
  '0002': ['clientesUnicos'],
  '0089': ['comandasServico', 'comandasProdutoServico', 'comandasProduto'],
  '0012': ['clientesNovos'],
  '0041': ['produtosVendidos', 'faturamentoProdutos', 'profissionais.produtos'],
  '0326': ['agendamentosPreferenciais', 'semPreferencia', 'profissionais.agendamentosPorProfissional'],
  '0062': ['pacotesVendidosTotal', 'valorPacotes', 'porPacote'],
  '0032': ['extras'],
  '0088': ['faturamentoDiario'],
  '0021': ['profissionais.faturamento'],
  '0123': ['profissionais.comissoes'],
  '0225': ['profissionais.pacotes'],
  '0064': ['_comandasPacoteTotal', 'profissionais.comandasPacotePorProf'],
  '0031p': ['profissionais.extras0031PorProfissional', 'profissionais.extrasPacote0031PorProfissional'],
  '0033': ['profissionais.tabelaPrecos0033'],
  '0063': [],
  '0229': ['profissionais.avecListCompleta'],
  '0065': ['profissionais.pacotesPorProfissional'],
  '0024': ['profissionais.extrasPorProfissional'],
  '0007': ['taxaRetorno'],
};

// Relatórios cujo resultado por PESSOA chega em vários pedidos (um por
// profissional): a herança mescla o mapa antigo com o novo.
const FAN_OUT = new Set(['0065', '0024']);

// Grupo do crédito de consumo de pacote (extras por profissional nos duelos e
// no "Faturamento de Serviços Extras").
export const GRUPO_CREDITO_PACOTE = ['0031p', '0063', '0064', '0033'];

// ─── leitura ────────────────────────────────────────────────────────────────

const numUtil = (v) => {
  if (v === null || v === undefined || v === '') return false;
  if (typeof v === 'number') return Number.isFinite(v) && v !== 0;
  if (typeof v === 'string') { const n = Number(v.replace(',', '.')); return Number.isFinite(n) && n !== 0; }
  return false;
};

/**
 * 200 válido sem nada: sem linha de total útil (total ausente, [] ou linhas cujos
 * valores numéricos são todos nulos/vazios/0) E sem linhas em result. Para o
 * fetchAllPages (array) vazio ⇔ length 0; para { result: rows } ⇔ rows vazio.
 */
export function respostaVazia(valor) {
  if (Array.isArray(valor)) return valor.length === 0;
  if (!valor || typeof valor !== 'object') return true;
  const result = Array.isArray(valor.result) ? valor.result : [];
  if (result.length > 0) return false;
  const total = valor.total;
  if (!Array.isArray(total) || total.length === 0) return true;
  return !total.some(linha => linha && typeof linha === 'object' && Object.values(linha).some(numUtil));
}

/** 'falhou' | 'vazio' | 'herdado' | 'ok' | 'nao_pedido' | 'desconhecido' (cache antigo, tratado como ok). */
export function estadoDoRelatorio(unit, code) {
  const c = unit?._coleta;
  if (!c || typeof c !== 'object') return 'desconhecido';
  if (c.falhou && c.falhou[code]) return 'falhou';
  if (c.herdado && c.herdado[code]) return 'herdado';
  if (Array.isArray(c.vazio) && c.vazio.includes(code)) return 'vazio';
  if (Array.isArray(c.pedidos) && c.pedidos.includes(code)) return 'ok';
  return 'nao_pedido';
}
export const relatorioFalhou = (unit, code) => estadoDoRelatorio(unit, code) === 'falhou';

/** Unidade (ou retorno do syncAllUnitsRange) tem alguma falha? */
export function temFalhas(obj) {
  if (!obj || typeof obj !== 'object') return false;
  if (obj.units && typeof obj.units === 'object') {
    return Object.values(obj.units).some(u => !u || u._error || (Array.isArray(u._falhas) && u._falhas.length > 0));
  }
  return !!obj._error || (Array.isArray(obj._falhas) && obj._falhas.length > 0);
}

/**
 * Mês do cache com alguma unidade com falha (não vira snapshot permanente).
 * `ignorarLeque` (revisão 23/10/2026, achados 4 e 11): a falha só de alguns
 * pedidos do leque por pessoa (0024/0065 'parcial') não segura o snapshot nem
 * dispara a re-sincronização do mês fechado: a herança já guardou a lista de
 * quem tinha (e refazer o mês inteiro por isso era o laço de ~40 pedidos por
 * unidade a cada abertura).
 */
export function mesTemFalhas(monthData, isSnapshot = false, { ignorarLeque = false } = {}) {
  const units = isSnapshot ? monthData : monthData?.units;
  if (!units || typeof units !== 'object') return false;
  return Object.values(units).some(u => u && Array.isArray(u._falhas)
    && u._falhas.some(code => !(ignorarLeque && falhaSoDeLeque(u, code))));
}

/** Grupos que precisam ir juntos na nova tentativa. */
export function expandirGruposRetentativa(falhas, { withExtras0031 = false } = {}) {
  const s = new Set(falhas || []);
  if (s.has('0089') || s.has('0002')) { s.add('0089'); s.add('0002'); }       // régua dos atendidos
  if (withExtras0031 && GRUPO_CREDITO_PACOTE.some(c => s.has(c))) GRUPO_CREDITO_PACOTE.forEach(c => s.add(c));
  if (s.has('0229')) s.add('0065');
  return [...s].sort();
}

// ─── caminhos 'a.b' ─────────────────────────────────────────────────────────
function lerCaminho(obj, caminho) {
  return caminho.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);
}
function gravarCaminho(obj, caminho, valor) {
  const partes = caminho.split('.');
  let o = obj;
  for (let i = 0; i < partes.length - 1; i++) {
    const k = partes[i];
    o[k] = (o[k] && typeof o[k] === 'object') ? { ...o[k] } : {};
    o = o[k];
  }
  o[partes[partes.length - 1]] = valor;
}
const valorUtil = (v) => {
  if (v === null || v === undefined) return false;
  if (typeof v === 'number') return v > 0;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v).length > 0;
  return !!v;
};

// ─── derivados null-safe ────────────────────────────────────────────────────
// Régua do ticket médio:
//   'ajustado' (a do syncUnitRange): faturamento ÷ (atendidos − comandas de pacote);
//   'bruto' (a do mergeMaxWins do cache mensal): faturamento ÷ atendidos.
// Revisão 23/10/2026 (achado 5): com `antes`, só recalcula o derivado cujo campo
// de entrada MUDOU (ou que ficou null com todas as entradas presentes). Assim o
// ticket não troca de régua só porque houve uma falha que nada tem a ver com ele
// (ex.: um 0024 parcial no cache mensal).
const ENTRADAS_DERIVADO = {
  geracaoDemanda: () => ['semPreferencia', 'clientesNovos'],
  ticketMedio: (regra) => (regra === 'bruto' ? ['faturamento', 'visitas'] : ['faturamento', 'visitas', '_comandasPacoteTotal']),
  ticketMedioBruto: () => ['faturamento', 'visitas'],
  tracaoVendas: () => ['servicosRealizados', 'visitas'],
};
export function rederivarUnidade(u, { antes = null, regraTicket = 'ajustado' } = {}) {
  if (!u || typeof u !== 'object') return u;
  const out = { ...u };
  const precisa = (d) => {
    const entradas = ENTRADAS_DERIVADO[d](regraTicket);
    if (!antes) return true;
    if (entradas.some(c => antes[c] !== out[c])) return true;
    return out[d] == null && entradas.every(c => out[c] != null);
  };
  const fat = out.faturamento, vis = out.visitas, svc = out.servicosRealizados, pac = out._comandasPacoteTotal;
  if (precisa('geracaoDemanda')) out.geracaoDemanda = (out.semPreferencia == null || out.clientesNovos == null) ? null : out.semPreferencia + out.clientesNovos;
  if (precisa('ticketMedio')) {
    if (regraTicket === 'bruto') out.ticketMedio = (fat == null || vis == null) ? null : (vis > 0 ? fat / vis : 0);
    else if (fat == null || vis == null || pac == null) out.ticketMedio = null;
    else { const divisor = Math.max(vis - pac, 0); out.ticketMedio = divisor > 0 ? fat / divisor : 0; }
  }
  if (precisa('ticketMedioBruto')) out.ticketMedioBruto = (fat == null || vis == null) ? null : (vis > 0 ? fat / vis : 0);
  if (precisa('tracaoVendas')) out.tracaoVendas = (svc == null || vis == null) ? null : (vis > 0 ? svc / vis : 0);
  return out;
}

// Lista por pessoa vazia (o pedido daquela pessoa falhou, ou veio vazio)?
const listaVazia = (v) => v == null || (Array.isArray(v) && v.length === 0);

/**
 * Para cada código que falhou em `next`, copia de `prev` (coleta anterior do
 * MESMO período/mês) os campos desse relatório e move o código de `falhou`
 * para `herdado[código] = ISO da coleta que trouxe o valor`.
 *   - prev com o código ok/vazio → herda (ts = prev._fetchedAt);
 *   - prev com o código herdado → só com aceitarHerdado (cache mensal), e o ts
 *     é o ORIGINAL, então a idade máxima vale para a cadeia inteira entre
 *     aparelhos (sem cadeia infinita). No período (A4b) aceitarHerdado=false;
 *   - prev antigo sem _coleta → só valores úteis (> 0 / lista não vazia);
 *   - idade (agora − ts) acima de maxIdadeMs → não herda.
 * `apenas`: restringe aos códigos listados. Devolve o MESMO objeto quando nada
 * muda; senão um objeto novo com os derivados recalculados (só os que tiveram
 * campo de entrada mudado, na régua `regraTicket`).
 * Leque por pessoa (0024, 0065): a pessoa cujo pedido falhou (lista vazia no
 * novo) fica com a lista do anterior; nunca troca lista cheia por vazia.
 */
export function protegerCamposComFalha(prev, next, { agora = Date.now(), maxIdadeMs = Infinity, aceitarHerdado = false, apenas = null, regraTicket = 'ajustado' } = {}) {
  if (!next || typeof next !== 'object' || next._error) return next;
  const falhas = Array.isArray(next._falhas) ? next._falhas : [];
  const alvo = apenas ? new Set(apenas) : null;
  const candidatos = falhas.filter(c => !alvo || alvo.has(c));
  if (!prev || typeof prev !== 'object' || prev._error || candidatos.length === 0) return next;
  let out = null;
  const garantir = () => {
    if (!out) {
      out = { ...next, profissionais: { ...(next.profissionais || {}) } };
      out._coleta = { ...(next._coleta || {}), falhou: { ...(next._coleta?.falhou || {}) }, herdado: { ...(next._coleta?.herdado || {}) } };
    }
    return out;
  };
  for (const code of candidatos) {
    const est = estadoDoRelatorio(prev, code);
    // Cache mensal (aceitarHerdado), leque por pessoa cujo anterior TAMBÉM foi
    // parcial (achado 11): as listas de quem respondeu (ou herdou) no anterior
    // valem; mescla por pessoa sem tirar o código de falhou (não dá para saber se
    // todos os que falharam agora estavam lá). No período (A4b) a regra é a estrita.
    if (aceitarHerdado && FAN_OUT.has(code) && est === 'falhou' && prev._coleta.falhou[code]?.motivo === 'parcial') {
      const idadeP = agora - Date.parse(prev._fetchedAt);
      if (!Number.isFinite(idadeP) || idadeP > maxIdadeMs) continue;
      for (const c of (CAMPOS_DO_RELATORIO[code] || [])) {
        const antigo = lerCaminho(prev, c);
        if (!antigo || typeof antigo !== 'object') continue;
        const novo = lerCaminho(out || next, c) || {};
        let mudou = false;
        const mesclado = { ...novo };
        for (const [nome, v] of Object.entries(antigo)) {
          if (!listaVazia(v) && listaVazia(mesclado[nome])) { mesclado[nome] = v; mudou = true; }
        }
        if (mudou) gravarCaminho(garantir(), c, mesclado);
      }
      continue;
    }
    let ts = null;
    let soUteis = false;
    if (est === 'ok' || est === 'vazio') ts = prev._fetchedAt;
    else if (est === 'herdado' && aceitarHerdado) ts = prev._coleta.herdado[code];
    else if (est === 'desconhecido') { ts = prev._fetchedAt; soUteis = true; }
    if (!ts) continue;
    const idade = agora - Date.parse(ts);
    if (!Number.isFinite(idade) || idade > maxIdadeMs) continue;
    const campos = [...(CAMPOS_DO_RELATORIO[code] || [])];
    // visitas acompanha a fonte que a produziu
    if (code === '0089') {
      const fonteAntiga = prev._visitasFonte ?? (est === 'desconhecido'
        && ((Number(prev.comandasServico) || 0) + (Number(prev.comandasProdutoServico) || 0)) > 0 ? '0089' : null);
      // ATENDIDOS_SO_COMANDA_SERVICO: contagem fresca do 0031 paginado (mesma régua) não é trocada pela antiga
      const frescoDo0031 = ATENDIDOS_SO_COMANDA_SERVICO && next.visitas != null && next._visitasFonte === '0031p';
      if (fonteAntiga === '0089' && prev.visitas != null && !frescoDo0031) campos.push('visitas', '_visitasFonte');
    }
    // comandas do 0031 paginado herdadas só por elas mesmas
    if (ATENDIDOS_SO_COMANDA_SERVICO && code === '0031p' && next.visitas == null && prev._visitasFonte === '0031p' && prev.visitas != null) campos.push('visitas', '_visitasFonte');
    // o 0002 só deixa de devolver visitas junto da tela nova (ATENDIDOS_SEM_0002)
    if (!ATENDIDOS_SEM_0002 && code === '0002' && next.visitas == null && prev._visitasFonte === '0002' && prev.visitas != null) campos.push('visitas', '_visitasFonte');
    if (soUteis && !campos.some(c => c === '_visitasFonte' || valorUtil(lerCaminho(prev, c)))) continue;
    const o = garantir();
    for (const c of campos) {
      if (c === '_visitasFonte') { o._visitasFonte = prev._visitasFonte ?? '0089'; continue; }
      const antigo = lerCaminho(prev, c);
      if (antigo === undefined) continue;
      if (soUteis && !valorUtil(antigo)) continue;
      if (FAN_OUT.has(code)) {
        const novo = lerCaminho(o, c) || {};
        const mesclado = { ...(antigo || {}) };
        for (const [nome, v] of Object.entries(novo)) {
          // pedido daquela pessoa falhou (ou veio vazio) e o anterior tinha lista: fica o anterior
          if (listaVazia(v) && !listaVazia(mesclado[nome])) continue;
          mesclado[nome] = v;
        }
        gravarCaminho(o, c, mesclado);
      } else {
        gravarCaminho(o, c, antigo);
      }
    }
    delete o._coleta.falhou[code];
    o._coleta.herdado[code] = ts;
  }
  if (!out) return next;
  out._falhas = Object.keys(out._coleta.falhou).sort();
  return rederivarUnidade(out, { antes: next, regraTicket });
}

// O anterior tinha valor para este campo? (> 0; ou 0 vindo de um relatório que
// respondeu). null/ausente, ou 0 de cache antigo sem _coleta (podia ser falha
// virando 0), não conta.
function anteriorTinhaValor(prev, code, campo) {
  const v = prev?.[campo];
  if (v === null || v === undefined) return false;
  const n = Number(v);
  if (!Number.isFinite(n)) return false;
  if (n > 0) return true;
  const est = estadoDoRelatorio(prev, code);
  return n === 0 && (est === 'ok' || est === 'vazio' || est === 'herdado');
}
const tsDoAnterior = (prev, code) => prev?._coleta?.herdado?.[code] || prev?._fetchedAt || 'mantido';

/**
 * Depois do mergeMaxWins (que transforma null em número com Math.max), campo
 * cujo código CONTINUA em _falhas volta a null SÓ quando o anterior não tinha
 * valor (ausente, null, ou 0 de cache antigo). Revisão 23/10/2026 (achados 2 e
 * 10): quando o anterior tinha valor e o mergeMaxWins o manteve (herança
 * recusada só pela idade), o valor FICA, igual ao de antes desta rodada (a tela
 * antiga mostrava esse valor, não 0). O código sai de _falhas e vai para
 * _coleta.herdado com a data ORIGINAL da coleta que trouxe o valor e para
 * _coleta.mantido (só os números foram mantidos, não as listas), para a tela
 * nova poder avisar. visitas segue a mesma régua (sem fonte nenhuma: só volta a
 * null quando o anterior não tinha). Sem `prev` = como antes (tudo volta a null).
 * Mesmo objeto se nada muda.
 */
export function restaurarNulos(u, { prev = null, regraTicket = 'ajustado' } = {}) {
  if (!u || typeof u !== 'object' || u._error) return u;
  const falhas = Array.isArray(u._falhas) ? u._falhas : [];
  if (falhas.length === 0) return u;
  const mudar = {};
  const mantidos = [];
  for (const code of falhas) {
    const campos = CAMPOS_NULAVEIS[code] || [];
    if (campos.length === 0) continue;
    let manteve = true;
    for (const campo of campos) {
      if (u[campo] === null) { manteve = false; continue; }
      if (prev && anteriorTinhaValor(prev, code, campo) && Number(u[campo]) === Number(prev[campo])) continue;
      mudar[campo] = null;
      manteve = false;
    }
    if (manteve) mantidos.push(code);
  }
  if (u._visitasFonte == null && (falhas.includes('0089') || falhas.includes('0002')) && u.visitas !== null) {
    const pv = Number(prev?.visitas);
    if (prev && prev.visitas != null && pv > 0 && Number(u.visitas) === pv) mudar._visitasFonte = prev._visitasFonte ?? null;
    else mudar.visitas = null;
  }
  if (Object.keys(mudar).length === 0 && mantidos.length === 0) return u;
  const out = { ...u, ...mudar };
  if (mantidos.length) {
    const c = u._coleta || {};
    const falhou = { ...(c.falhou || {}) };
    const herdado = { ...(c.herdado || {}) };
    for (const code of mantidos) { delete falhou[code]; herdado[code] = tsDoAnterior(prev, code); }
    out._coleta = { ...c, falhou, herdado, mantido: [...new Set([...(c.mantido || []), ...mantidos])].sort() };
    out._falhas = Object.keys(falhou).sort();
  }
  return rederivarUnidade(out, { antes: u, regraTicket });
}

/**
 * Cache mensal (doSync e 12 meses): por unidade, herda o que falhou da coleta
 * anterior do MESMO mês e devolve null ao que continua faltando sem valor
 * anterior. Sem falha nenhuma devolve o MESMO objeto (o que vai para
 * fk_avec_cache é o de hoje).
 *   - mês corrente: a cadeia de herança para em 12 h (data original); passou
 *     disso, o número que o mergeMaxWins manteve continua (achado 2);
 *   - mês fechado (`mesFechado`): sem limite de idade (o mês não muda mais e o
 *     anterior é do mesmo mês fechado; achado 10);
 *   - `regraMaxWins` (o doSync sem force passou pelo mergeMaxWins): o ticket que
 *     precisar ser recalculado usa a régua do mergeMaxWins (faturamento ÷
 *     atendidos), a mesma do sync sem falha (achado 5).
 */
export const HERANCA_MES_MAX_MS = 12 * 3600_000;
export function protegerMes(prevMonth, nextMonth, { agora = Date.now(), maxIdadeMs = HERANCA_MES_MAX_MS, mesFechado = false, regraMaxWins = false } = {}) {
  if (!nextMonth || !nextMonth.units) return nextMonth;
  let units = null;
  for (const [k, u] of Object.entries(nextMonth.units)) {
    if (!u || u._error || !Array.isArray(u._falhas) || u._falhas.length === 0) continue;
    const prevU = prevMonth?.units?.[k];
    const regraTicket = regraMaxWins && prevU && !prevU._error ? 'bruto' : 'ajustado';
    let nu = protegerCamposComFalha(prevU, u, { agora, maxIdadeMs: mesFechado ? Infinity : maxIdadeMs, aceitarHerdado: true, regraTicket });
    nu = restaurarNulos(nu, { prev: prevU && !prevU._error ? prevU : null, regraTicket });
    nu = ticketSemComandasDePacote(nu);
    if (nu !== u) { if (!units) units = { ...nextMonth.units }; units[k] = nu; }
  }
  return units ? { ...nextMonth, units } : nextMonth;
}

// Cache mensal: com o 0064 faltando (sem herança), faturamento e atendidos
// presentes, o ticket da unidade é faturamento ÷ atendidos, o MESMO número de
// antes desta rodada (o 0064 que falhava contava 0 comandas de pacote) e a régua
// que o mergeMaxWins grava a cada 60 s. Sem isto o null virava 0 no "Por unidade"
// da tela antiga (prova de neutralidade da P2, achado 7). O '0064' continua em _falhas.
function ticketSemComandasDePacote(u) {
  if (!u || u._error || u.ticketMedio != null || u._comandasPacoteTotal != null) return u;
  const fat = u.faturamento, vis = u.visitas;
  if (fat == null || vis == null) return u;
  return { ...u, ticketMedio: vis > 0 ? fat / vis : 0 };
}

/** Leque por pessoa com falha só de alguns pedidos (0024/0065 'parcial'). */
export function falhaSoDeLeque(unit, code) {
  const f = unit?._coleta?.falhou?.[code];
  return FAN_OUT.has(code) && !!f && f.motivo === 'parcial';
}

/**
 * Unidade cortada pelo prazo da coleta (achado 1): nenhum relatório pedido
 * respondeu e pelo menos um falhou por 'prazo'. Vira { _error: 'prazo' } para
 * sair da soma da rede (a tela antiga cai no mensal, como já faz com _error).
 */
export function cortadaPeloPrazo(u) {
  const c = u?._coleta;
  if (!c || !Array.isArray(c.pedidos) || c.pedidos.length === 0 || !c.falhou) return false;
  if (!c.pedidos.every(code => c.falhou[code])) return false;
  return Object.values(c.falhou).some(f => f && f.motivo === 'prazo');
}
